import { useState, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Typography } from '@eventbrite/marmalade';

import styles from './Agenda.module.scss';
import { Tab } from './Content/Tab';
import { Content, ContentScrollHandle } from './Content/Content';
import { PageNavigation } from './PageNavigation/PageNavigation';
import { useIsMobile } from './_shared/useIsMobile';
import { MobilePopupLayout } from './MobilePopupLayout/MobilePopupLayout';
import { ShowAllButton } from './ShowAllButton/ShowAllButton';

interface Props {
  tabs?: Tab[];
}

export const Agenda: React.FC<Props> = ({ tabs }) => {
  const [showAllSlots, setShowAllSlots] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab | undefined>(tabs?.[0]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const contentRef = useRef<ContentScrollHandle>(null);
  const isMobile = useIsMobile();

  if (!tabs || tabs.length === 0) {
    return null;
  }

  const handleScrollPrev = () => {
    contentRef.current?.scrollToPrev();
  };

  const handleScrollNext = () => {
    contentRef.current?.scrollToNext();
  };

  const handleScrollStateChange = (prev: boolean, next: boolean) => {
    setCanScrollPrev(prev);
    setCanScrollNext(next);
  };

  return (
    <MobilePopupLayout opened={showAllSlots} onClose={() => setShowAllSlots(false)}>
      <div className={styles.agenda} data-testid="agenda">
        <div className={styles.header}>
          <Typography as="h3" variant="heading-sm" className={styles.title}>
            <FormattedMessage id="Agenda" />
          </Typography>
          <PageNavigation
            canScrollPrev={canScrollPrev}
            canScrollNext={canScrollNext}
            onScrollPrev={handleScrollPrev}
            onScrollNext={handleScrollNext}
          />
        </div>
        <Content
          ref={contentRef}
          onTabChange={(tab) => {
            setActiveTab(tab);
          }}
          onScrollStateChange={handleScrollStateChange}
          tabs={tabs}
          showTabs={tabs.length > 1}
          showAllSlots={showAllSlots}
        />
        {(!showAllSlots || !isMobile) && <ShowAllButton slots={activeTab?.slots || []} onChange={setShowAllSlots} />}
      </div>
    </MobilePopupLayout>
  );
};
